import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Image as ImgIcon, Upload, Loader2, Camera, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import queue from "../lib/uploadQueue";

const CAT_META = {
  before:     { label: "Before",     accent: "var(--ink-muted)" },
  during:     { label: "During",     accent: "var(--brand)" },
  after:      { label: "After",      accent: "var(--good)" },
  defect:     { label: "Defect",     accent: "var(--bad)" },
  variation:  { label: "Variation",  accent: "var(--warn)" },
  weather:    { label: "Weather",    accent: "#0EA5E9" },
  completion: { label: "Completion", accent: "var(--accent)" },
  inspection: { label: "Inspection", accent: "var(--ink-soft)" },
  damage:     { label: "Damage",     accent: "var(--bad)" },
  progress:   { label: "Progress",   accent: "var(--brand)" },
  safety:     { label: "Safety",     accent: "var(--warn)" },
  qbcc:       { label: "QBCC",       accent: "var(--ink-soft)" },
  warranty:   { label: "Warranty",   accent: "var(--ink-soft)" },
  other:      { label: "Other",      accent: "var(--ink-muted)" },
};

const PRIMARY_TABS = ["before", "during", "after", "defect", "variation", "weather", "completion"];

/**
 * PhotoGallery — tag-grouped operational visual memory.
 *
 * Props:
 *   projectId (string)
 *   uploadLinkedKind (optional) — stamps uploads with linked_kind / linked_id
 *   uploadLinkedId   (optional)
 *   defaultCategory  (optional) — defaults to first non-empty bucket
 *   compact (bool) — smaller heading for use inside step cards
 */
export default function PhotoGallery({
  projectId, uploadLinkedKind, uploadLinkedId, defaultCategory, compact = false,
}) {
  const [groups, setGroups] = useState({});
  const [categories, setCategories] = useState(PRIMARY_TABS);
  const [activeCat, setActiveCat] = useState(defaultCategory || "before");
  const [uploadCat, setUploadCat] = useState(defaultCategory || "during");
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [filter, setFilter] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/uploads/grouped?project_id=${projectId}`);
      setGroups(data.groups || {});
      setCategories(data.categories || PRIMARY_TABS);
    } catch {/* ignore */}
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Subscribe to queue so newly-flushed uploads refresh the gallery
  useEffect(() => {
    const unsub = queue.subscribe((s) => {
      // when queue drained to zero we likely just synced — refresh
      if (!s.syncing && s.items.length === 0) load();
    });
    return unsub;
  }, [load]);

  const all = useMemo(() => {
    const arr = Object.values(groups).flat();
    if (!filter) return arr;
    const q = filter.toLowerCase();
    return arr.filter((u) =>
      (u.original_filename || "").toLowerCase().includes(q) ||
      (u.caption || "").toLowerCase().includes(q) ||
      (u.category || "").toLowerCase().includes(q)
    );
  }, [groups, filter]);

  const list = useMemo(() => {
    if (activeCat === "all") return all;
    return (groups[activeCat] || []).filter((u) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (u.original_filename || "").toLowerCase().includes(q) ||
             (u.caption || "").toLowerCase().includes(q);
    });
  }, [activeCat, groups, all, filter]);

  const tabsWithCounts = PRIMARY_TABS.map((c) => ({
    key: c,
    label: CAT_META[c]?.label || c,
    count: (groups[c] || []).length,
    accent: CAT_META[c]?.accent || "var(--ink-muted)",
  }));

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      try {
        if (navigator.onLine === false) {
          // queue offline
          await queue.enqueueUpload({
            projectId, category: uploadCat, file,
            linkedKind: uploadLinkedKind, linkedId: uploadLinkedId,
          });
          toast.message(`Queued ${file.name} for sync`);
        } else {
          const fd = new FormData();
          fd.append("project_id", projectId);
          fd.append("category", uploadCat);
          if (uploadLinkedKind) fd.append("linked_kind", uploadLinkedKind);
          if (uploadLinkedId) fd.append("linked_id", uploadLinkedId);
          fd.append("file", file);
          await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      } catch (err) {
        // fallback to queue on network error
        try {
          await queue.enqueueUpload({
            projectId, category: uploadCat, file,
            linkedKind: uploadLinkedKind, linkedId: uploadLinkedId,
          });
          toast.message(`Queued ${file.name} — will retry`);
        } catch {
          toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
      }
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    setActiveCat(uploadCat);
    await load();
    toast.success(`${files.length} file${files.length !== 1 ? "s" : ""} added`);
  };

  return (
    <div className="surface-1" data-testid="photo-gallery">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <ImgIcon size={14} style={{ color: "var(--brand)" }} />
        <div className={(compact ? "text-[12px]" : "text-[13.5px]") + " font-bold"} style={{ color: "var(--ink)" }}>
          Operational evidence
        </div>
        <div className="text-[10.5px] ml-1" style={{ color: "var(--ink-muted)" }}>
          {Object.values(groups).reduce((s, a) => s + a.length, 0)} photos
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <Filter size={11} style={{ color: "var(--ink-muted)" }} />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            data-testid="gallery-filter"
            className="text-[11px] py-1 px-2"
            style={{ width: 110, background: "var(--bg-elev-2)", border: "1px solid var(--line-strong)", borderRadius: 4 }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="gallery-tabs">
        {tabsWithCounts.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveCat(t.key)}
            data-testid={`tab-${t.key}`}
            className={"inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-extrabold uppercase tracking-[0.10em] " +
              (t.count > 0 ? "" : "opacity-50")}
            style={{
              background: activeCat === t.key ? t.accent : "var(--bg-elev-2)",
              color: activeCat === t.key ? "#fff" : "var(--ink-soft)",
              border: `1px solid ${activeCat === t.key ? t.accent : "var(--line-strong)"}`,
            }}
          >
            {t.label}
            <span className="font-mono text-[9.5px]"
                  style={{ opacity: 0.75 }}>{t.count}</span>
          </button>
        ))}
        <button
          onClick={() => setActiveCat("all")}
          data-testid="tab-all"
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-extrabold uppercase tracking-[0.10em]"
          style={{
            background: activeCat === "all" ? "var(--brand)" : "var(--bg-elev-2)",
            color: activeCat === "all" ? "#fff" : "var(--ink-soft)",
            border: `1px solid ${activeCat === "all" ? "var(--brand)" : "var(--line-strong)"}`,
          }}
        >
          All
          <span className="font-mono text-[9.5px]" style={{ opacity: 0.75 }}>{all.length}</span>
        </button>
      </div>

      {/* Upload bar */}
      <div className="px-4 pb-3 flex items-center gap-2" data-testid="gallery-upload-bar">
        <select
          value={uploadCat}
          onChange={(e) => setUploadCat(e.target.value)}
          data-testid="upload-tag-select"
          className="text-[11px] py-1.5 px-2"
          style={{ background: "var(--bg-elev-2)", border: "1px solid var(--line-strong)", borderRadius: 4, minHeight: 36 }}
        >
          {(categories || PRIMARY_TABS).map((c) => (
            <option key={c} value={c}>{CAT_META[c]?.label || c}</option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onPick}
          data-testid="gallery-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="gallery-upload-btn"
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11.5px] font-extrabold uppercase tracking-[0.10em]"
          style={{ background: "var(--accent)", color: "#fff", minHeight: 36 }}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
          Take / upload photo
        </button>
      </div>

      {/* Grid */}
      {list.length === 0 ? (
        <div className="px-4 pb-4 text-[11px] text-center" style={{ color: "var(--ink-muted)" }} data-testid="gallery-empty">
          No {activeCat === "all" ? "" : CAT_META[activeCat]?.label?.toLowerCase() + " "}photos yet. Tap the camera to add evidence.
        </div>
      ) : (
        <div className="px-4 pb-4 grid grid-cols-3 gap-1.5" data-testid="gallery-grid">
          {list.map((u) => (
            <button
              key={u.id}
              onClick={() => u.is_image && setLightbox(u)}
              data-testid={`gallery-thumb-${u.id}`}
              className="relative aspect-square overflow-hidden cursor-pointer"
              style={{ background: "var(--bg-elev-2)", borderRadius: 4 }}
            >
              {u.is_image ? (
                <>
                  <span
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background: "linear-gradient(135deg, var(--bg-elev-2) 0%, var(--bg-elev-3) 100%)",
                    }}
                  />
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${u.url}`}
                    alt={u.original_filename}
                    className="relative w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
                    style={{ opacity: 0, transition: "opacity .25s ease-out" }}
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImgIcon size={18} style={{ color: "var(--ink-faint)" }} />
                </div>
              )}
              <span
                className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-[0.08em]"
                style={{
                  background: (CAT_META[u.category]?.accent || "var(--ink-muted)") + "ee",
                  color: "#fff",
                }}
              >
                {CAT_META[u.category]?.label || u.category}
              </span>
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(14,14,24,0.88)" }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-md text-white/80 hover:text-white"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            data-testid="lightbox-close"
            style={{ background: "rgba(255,255,255,0.10)" }}
          >
            <X size={18} />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh]">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`}
              alt=""
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
            <div className="text-center mt-3 text-[12px] text-white/80">
              <span className="px-2 py-0.5 rounded mr-2"
                    style={{ background: CAT_META[lightbox.category]?.accent || "var(--ink-muted)" }}>
                {CAT_META[lightbox.category]?.label || lightbox.category}
              </span>
              {lightbox.caption || lightbox.original_filename}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
